import { motion } from 'framer-motion';
import { Users, Calendar, MapPin, Clock, AlertCircle, CheckCircle2, Film, Star } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Link } from 'react-router-dom';

const Auditions = () => {
  const currentAuditions = [
    {
      id: 1,
      title: 'Lead Male Actor',
      project: 'Upcoming Hindi Film (2026)',
      status: 'Open',
      ageRange: '25-35 years',
      gender: 'Male',
      languages: ['Hindi', 'English'],
      requirements: [
        'Strong screen presence',
        'Acting experience in films/theatre preferred',
        'Ability to perform intense emotional scenes',
        'Comfortable with action sequences',
      ],
      location: 'Kolkata / Mumbai',
      deadline: 'To be announced',
    },
    {
      id: 2,
      title: 'Lead Female Actor',
      project: 'Upcoming Hindi Film (2026)',
      status: 'Open',
      ageRange: '22-32 years',
      gender: 'Female',
      languages: ['Hindi', 'English'],
      requirements: [
        'Expressive and versatile acting skills',
        'Professional training or experience preferred',
        'Comfortable with dramatic and romantic sequences',
        'Strong dialogue delivery',
      ],
      location: 'Kolkata / Mumbai',
      deadline: 'To be announced',
    },
    {
      id: 3,
      title: 'Supporting Cast - Multiple Roles',
      project: 'Upcoming Hindi Film (2026)',
      status: 'Open',
      ageRange: '18-50 years',
      gender: 'Male & Female',
      languages: ['Hindi', 'Bengali'],
      requirements: [
        'Various character roles available',
        'Fresh faces welcome',
        'Theatre background appreciated',
        'Flexible with shooting schedules',
      ],
      location: 'Kolkata',
      deadline: 'Rolling basis',
    },
  ];

  const pastProductions = [
    {
      title: 'Aprokashito (2024)',
      cast: ['Aryann Bhowmik', 'Joy Sengupta', 'Debleena Dutt'],
      status: 'Released',
    },
  ];

  return (
    <div data-testid="auditions-page" className="min-h-screen bg-[#050505] pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-96 h-96 bg-[#E50914]/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="inline-block px-4 py-2 bg-[#D4AF37]/20 border border-[#D4AF37]/50 rounded-full text-[#D4AF37] text-sm tracking-widest uppercase mb-6">
              Join Our Team
            </span>
            <h1 className="font-heading text-6xl md:text-8xl text-white tracking-tight mb-6">
              AUDITION
              <span className="block text-gold-gradient">UPDATES</span>
            </h1>
            <p className="text-[#A3A3A3] text-lg max-w-2xl mx-auto">
              We're always looking for talented individuals to bring our stories to life. 
              Check out our current casting calls below.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Current Auditions */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-4">
              <Film className="text-[#E50914]" size={32} />
              <h2 className="font-heading text-3xl md:text-4xl text-white tracking-wider">
                CURRENT CASTING CALLS
              </h2>
            </div>
            <p className="text-[#A3A3A3]">
              Active auditions for our upcoming productions
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {currentAuditions.map((audition, index) => (
              <motion.div
                key={audition.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-[#121212] border border-white/5 rounded-xl p-8 hover:border-[#E50914]/30 transition-colors"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h3 className="text-white font-semibold text-xl mb-2">{audition.title}</h3>
                    <p className="text-[#D4AF37] font-accent italic">{audition.project}</p>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30 uppercase tracking-wider text-xs">
                    {audition.status}
                  </Badge>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <Users className="text-[#D4AF37] flex-shrink-0" size={18} />
                    <div>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Gender</p>
                      <p className="text-white text-sm">{audition.gender}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="text-[#D4AF37] flex-shrink-0" size={18} />
                    <div>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Age Range</p>
                      <p className="text-white text-sm">{audition.ageRange}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <MapPin className="text-[#D4AF37] flex-shrink-0" size={18} />
                    <div>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Location</p>
                      <p className="text-white text-sm">{audition.location}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="text-[#D4AF37] flex-shrink-0" size={18} />
                    <div>
                      <p className="text-xs text-[#A3A3A3] uppercase tracking-wider">Deadline</p>
                      <p className="text-white text-sm">{audition.deadline}</p>
                    </div>
                  </div>
                </div>

                {/* Languages */}
                <div className="mb-6">
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-2">Languages Required</p>
                  <div className="flex flex-wrap gap-2">
                    {audition.languages.map((lang) => (
                      <span key={lang} className="px-3 py-1 bg-[#E50914]/10 text-[#E50914] text-xs rounded-full">
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Requirements */}
                <div className="mb-6">
                  <p className="text-xs text-[#A3A3A3] uppercase tracking-wider mb-3">Requirements</p>
                  <ul className="space-y-2">
                    {audition.requirements.map((req, i) => (
                      <li key={i} className="flex items-start gap-2 text-[#A3A3A3] text-sm">
                        <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={14} />
                        {req}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Apply Button */}
                <Link to="/contact">
                  <Button
                    data-testid={`apply-audition-${audition.id}`}
                    className="w-full bg-[#E50914] hover:bg-[#B20710] text-white py-6 text-sm tracking-widest uppercase font-semibold rounded-sm"
                  >
                    Express Interest
                  </Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Apply */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Application Process</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mt-4">
              HOW TO APPLY
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                step: '01',
                title: 'Send Your Portfolio',
                description: 'Email your portfolio with recent photos, resume, and acting reel (if available) to our contact email.',
              },
              {
                step: '02',
                title: 'Initial Screening',
                description: 'Our casting team reviews all submissions and shortlists candidates based on role requirements.',
              },
              {
                step: '03',
                title: 'Audition Call',
                description: 'Shortlisted candidates are invited for in-person or virtual auditions with our creative team.',
              },
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center"
              >
                <span className="font-heading text-6xl text-[#E50914]/30">{item.step}</span>
                <h3 className="text-white font-semibold text-lg mt-4 mb-3">{item.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-16 bg-gradient-to-r from-[#121212] to-[#1a1a1a] rounded-2xl p-8 md:p-12 max-w-3xl mx-auto text-center"
          >
            <Star className="text-[#D4AF37] mx-auto mb-4" size={32} />
            <h3 className="font-heading text-2xl text-white tracking-wider mb-4">
              READY TO AUDITION?
            </h3>
            <p className="text-[#A3A3A3] mb-6">
              Send your application materials to:
            </p>
            <a
              href="mailto:swarnalientertainment.social@gmail.com"
              className="text-[#D4AF37] text-lg md:text-xl font-semibold hover:underline break-all"
            >
              swarnalientertainment.social@gmail.com
            </a>
            <p className="text-[#A3A3A3] text-sm mt-4">
              Subject line: "Audition Application - [Role Name]"
            </p>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-16 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-[#E50914]/10 border border-[#E50914]/30 rounded-xl p-8 max-w-3xl mx-auto"
          >
            <div className="flex items-start gap-4">
              <AlertCircle className="text-[#E50914] flex-shrink-0 mt-1" size={24} />
              <div>
                <h3 className="text-white font-semibold text-lg mb-2">Important Notice</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">
                  Swarnali Entertainment never charges any fee for auditions or casting. 
                  Beware of fraudulent agencies claiming to represent us. All official 
                  communications will come from our verified email address only. 
                  Report any suspicious activity to us immediately.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Past Productions */}
      <section className="py-24 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="text-[#E50914] text-sm tracking-[0.3em] uppercase font-semibold">Our Legacy</span>
            <h2 className="font-heading text-4xl md:text-5xl text-white tracking-wide mt-4">
              PAST PRODUCTIONS
            </h2>
          </motion.div>

          <div className="max-w-2xl mx-auto">
            {pastProductions.map((production, index) => (
              <motion.div
                key={production.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4 }}
                className="bg-[#121212] border border-white/5 rounded-xl p-8 text-center"
              >
                <Badge className="bg-[#D4AF37]/20 text-[#D4AF37] border-[#D4AF37]/30 uppercase tracking-wider text-xs mb-4">
                  {production.status}
                </Badge>
                <h3 className="font-heading text-3xl text-white tracking-wider mb-4">
                  {production.title}
                </h3>
                <p className="text-[#A3A3A3] mb-4">Featuring</p>
                <div className="flex flex-wrap justify-center gap-3">
                  {production.cast.map((actor) => (
                    <span key={actor} className="px-4 py-2 bg-white/5 border border-white/10 rounded text-white text-sm">
                      {actor}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Auditions;
